import React from 'react';
import PropTypes from 'prop-types';
import dataSorter from './data-sorter';
import {Table, Column, AutoSizer} from 'react-virtualized';
import HeaderRenderer from './renderers/header-renderer';
import StringRenderer from './renderers/string-renderer';
import BoolRenderer from './renderers/bool-renderer';
import UnixTimeRenderer from './renderers/unix-time-renderer';
import StringFilterer from './filterers/string-filterer';
import UnixTimeFilterer from './filterers/unix-time-filterer';
import {buildFilterableStrings, getFilteredIndexes} from './data-filterer';

const HEADER_HEIGHT = 44;
const ROW_HEIGHT = 40;

export {Column};

function determineIndexesForDisplay(filterBy, rowsFilterableText, sortedIndexes) {
  const displayIndexes = [];
  const filteredIndexes = getFilteredIndexes(filterBy, rowsFilterableText);
  sortedIndexes.forEach(originalPosition => {
    if (filteredIndexes.indexOf(originalPosition) > -1) {
      displayIndexes.push(originalPosition);
    }
  });
  return displayIndexes;
}

export default function VirtualizedTable(props) {
  const {
    data,
    children,
    sortBy: defaultSortBy,
    sortDirection: defaultSortDirection,
    filter: filterBy,
    filterable,
    ...rest
  } = props;
  const [sortBy, setSortBy] = React.useState(defaultSortBy);
  const [sortDirection, setSortDirection] = React.useState(defaultSortDirection);

  const onSort = React.useCallback(options => {
    setSortBy(options.sortBy);
    setSortDirection(options.sortDirection);
  }, []);

  const rowsFilterableStrings = React.useMemo(() => {
    return filterable ? buildFilterableStrings(data, children) : null;
  }, [data, filterable]);

  const sortedIndexes = React.useMemo(() => {
    const sortColumn = children.find(child => child.props.dataKey === sortBy);
    return dataSorter(data, sortBy, sortDirection, sortColumn);
  }, [data, sortBy, sortDirection]);

  const displayIndexMap = React.useMemo(() => {
    if (rowsFilterableStrings) {
      return determineIndexesForDisplay(filterBy, rowsFilterableStrings, sortedIndexes);
    } else {
      return [...sortedIndexes];
    }
  }, [data, rowsFilterableStrings, filterBy, rowsFilterableStrings, sortedIndexes]);

  const rowGetter = React.useCallback(
    ({index}) => {
      const originalPosition = displayIndexMap[index] ?? index;
      return data[originalPosition];
    },
    [data, displayIndexMap],
  );

  return (
    <AutoSizer>
      {({width, height}) => (
        <Table
          width={width}
          height={height}
          headerHeight={HEADER_HEIGHT}
          rowHeight={ROW_HEIGHT}
          rowGetter={rowGetter}
          rowCount={displayIndexMap.length}
          sort={onSort}
          sortBy={sortBy}
          sortDirection={sortDirection}
          {...rest}
        >
          {children}
        </Table>
      )}
    </AutoSizer>
  );
}

VirtualizedTable.defaultProps = {
  filterable: false,
};

VirtualizedTable.propTypes = {
  /** The data to display */
  data: PropTypes.array.isRequired,
  /** Column nodes */
  children: PropTypes.node,
  /** The field name to sort by for the initial sort. Leaving this undefined will default to the natural order of the `data` array. */
  sortBy: PropTypes.string,
  /** The initial sort direction. 'ASC' or 'DESC'. */
  sortDirection: PropTypes.oneOf(['ASC', 'DESC']),
  /** Optionally a string to filter entries by. */
  filter: PropTypes.string,
  /** Whether or not this table will be searchable. When enabled, all data will be rendered to strings and indexed to facilitate filtering. */
  filterable: PropTypes.bool,
};

VirtualizedTable.Column = Column;
VirtualizedTable.renderers = {
  Header: HeaderRenderer,
  String: StringRenderer,
  UnixTime: UnixTimeRenderer,
  Bool: BoolRenderer,
};
VirtualizedTable.filterers = {
  String: StringFilterer,
  UnixTime: UnixTimeFilterer,
};
